import { BooleanInput } from '@angular/cdk/coercion';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { ModuleDropdownShortcutService } from 'app/shared/services/module-dropdown-shortcut.service';
import {
  MegaMenuBranchRegistryService,
  MegaPeerFocusMode,
} from './mega-menu-branch-registry.service';
import { MegaMenuCoordinatorService } from './mega-menu-coordinator.service';
import { filter } from 'rxjs/operators';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { WorkspaceTabsService } from 'app/layout/common/workspace-tabs/workspace-tabs.service';

/** One column in the mega menu (feature or logical group). */
export interface MegaMenuColumn {
  id: string;
  title: string;
  /** Flat links and optional titled sub-groups for nested collapsables */
  sections: MegaMenuSection[];
}

export interface MegaMenuSection {
  subtitle?: string;
  links: FuseNavigationItem[];
}

@Component({
  selector: 'app-mega-module-menu-branch',
  templateUrl: './mega-module-menu-branch.component.html',
  styleUrls: ['./mega-module-menu-branch.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgTemplateOutlet,
    MatTooltipModule,
    MatIconModule,
    RouterLink,
    RouterLinkActive,
  ],
})
export class MegaModuleMenuBranchComponent implements OnChanges, OnInit, OnDestroy {
  /* eslint-disable @typescript-eslint/naming-convention */
  static ngAcceptInputType_child: BooleanInput;
  /* eslint-enable @typescript-eslint/naming-convention */

  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _fuseNavigationService = inject(FuseNavigationService);
  private readonly _moduleDropdownShortcuts = inject(ModuleDropdownShortcutService);
  private readonly _router = inject(Router);
  private readonly _overlay = inject(Overlay);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _megaCoordinator = inject(MegaMenuCoordinatorService);
  private readonly _peerRegistry = inject(MegaMenuBranchRegistryService);
  private readonly _elementRef = inject(ElementRef<HTMLElement>);
  private readonly _workspaceTabs = inject(WorkspaceTabsService);

  @Input() child = false;
  @Input() item: FuseNavigationItem;
  @Input() name: string;

  /** Must be `static: false` — `#triggerRef` lives inside `@if (!child)` so it does not exist at static query time. */
  @ViewChild('triggerRef') triggerRef: ElementRef<HTMLElement>;
  @ViewChild('megaTemplate', { static: true }) megaTemplate: TemplateRef<unknown>;

  private _fuseHorizontalNavigation: { onRefreshed: Observable<boolean> };
  private readonly _destroy$ = new Subject<void>();
  private _overlayRef: OverlayRef | null = null;

  /** Stable ref passed to MegaMenuCoordinatorService */
  private readonly _closeBound = (): void => {
    this._disposeOverlay();
  };

  megaOpen = false;
  hasActiveChild = false;
  columns: MegaMenuColumn[] = [];
  /** Vertical stacks laid out side-by-side (API order preserved within the split). */
  lanes: MegaMenuColumn[][] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if ('item' in changes || 'name' in changes) {
      this._updateHasActiveChild(this._router.url);
    }
  }

  ngOnInit(): void {
    this._fuseHorizontalNavigation =
      this._fuseNavigationService.getComponent(this.name);

    this._fuseHorizontalNavigation.onRefreshed
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._rebuildColumns();
        this._updateHasActiveChild(this._router.url);
        this._cdr.markForCheck();
      });

    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this._destroy$),
      )
      .subscribe((e: NavigationEnd) => {
        this._disposeOverlay();
        this._updateHasActiveChild(e.urlAfterRedirects || this._router.url);
      });

    this._updateHasActiveChild(this._router.url);
    this._rebuildColumns();

    if (!this.child) {
      this._peerRegistry.register({
        hostElement: this._elementRef.nativeElement,
        openFromPeerNav: (focus: MegaPeerFocusMode) =>
          this._openFromPeerNavigation(focus),
      });
    }

    this._moduleDropdownShortcuts.openDropdown$
      .pipe(takeUntil(this._destroy$))
      .subscribe((moduleId) => {
        if (!this.child && this.item?.id === moduleId) {
          setTimeout(() => {
            if (!this.megaOpen) {
              this.openMegaMenu();
            }
          }, 0);
        } else if (!this.child && this.megaOpen && this.item?.id !== moduleId) {
          this._disposeOverlay();
        }
      });
  }

  ngOnDestroy(): void {
    if (!this.child) {
      this._peerRegistry.unregister(this._elementRef.nativeElement);
    }
    this._disposeOverlay();
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.megaOpen) {
      this._disposeOverlay();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.megaOpen && this.columns.length > 0) {
      this.lanes = this._splitIntoLanes(this.columns);
      this._cdr.markForCheck();
    }
  }

  toggleMegaMenu(): void {
    if (this.megaOpen) {
      this._disposeOverlay();
    } else {
      this.openMegaMenu();
    }
  }

  /**
   * @param focusMode Where to put focus after open — default first link; `lastLaneFirst` for Left from next module.
   */
  openMegaMenu(focusMode: MegaPeerFocusMode = 'first'): void {
    if (this.child || this._overlayRef?.hasAttached()) {
      return;
    }
    this._rebuildColumns();
    if (this.columns.length === 0 || this.lanes.length === 0) {
      return;
    }

    this._megaCoordinator.openExclusive(this._closeBound);

    let el = this.triggerRef?.nativeElement;
    if (!el) {
      this._cdr.detectChanges();
      el = this.triggerRef?.nativeElement;
    }
    if (!el) {
      this._megaCoordinator.closed(this._closeBound);
      return;
    }

    const rect = el.getBoundingClientRect();
    /** Panel starts under the trigger (e.g. Masters), spans to the right edge of the viewport */
    const panelWidthPx = Math.max(240, window.innerWidth - rect.left);

    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(el)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetX: 0,
          offsetY: 0,
        },
      ]);

    this._overlayRef = this._overlay.create({
      positionStrategy,
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
      width: `${panelWidthPx}px`,
      maxWidth: `${panelWidthPx}px`,
      hasBackdrop: true,
      backdropClass: 'app-mega-menu-backdrop',
      panelClass: 'app-mega-menu-overlay-pane',
    });

    const portal = new TemplatePortal(this.megaTemplate, this._viewContainerRef);
    this._overlayRef.attach(portal);
    this._overlayRef
      .backdropClick()
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe(() => this._closeBound());

    this.megaOpen = true;
    this._cdr.markForCheck();

    queueMicrotask(() => {
      requestAnimationFrame(() => {
        if (focusMode === 'lastLaneFirst') {
          this._focusFirstLinkInLastLane();
        } else {
          this._focusFirstMegaMenuLink();
        }
      });
    });
  }

  /** Open this module’s mega menu from ArrowLeft/Right at a lane boundary (closes any other mega first). */
  private _openFromPeerNavigation(focus: MegaPeerFocusMode): void {
    if (this.child) {
      return;
    }
    if (this.megaOpen) {
      this._disposeOverlay();
    }
    this.openMegaMenu(focus);
  }

  /**
   * Arrow keys: Up/Down move along all links in DOM order; Left/Right jump to first
   * link in the previous/next lane (column).
   */
  onMegaMenuKeydown(event: KeyboardEvent): void {
    const key = event.key;
    if (
      key !== 'ArrowUp' &&
      key !== 'ArrowDown' &&
      key !== 'ArrowLeft' &&
      key !== 'ArrowRight'
    ) {
      return;
    }

    const shell = (event.currentTarget as HTMLElement)?.closest(
      '.app-mega-menu-shell',
    );
    if (!shell) {
      return;
    }

    const links = this._collectMegaMenuLinks(shell);
    if (!links.length) {
      return;
    }

    const active = document.activeElement as HTMLElement | null;
    let idx = active ? links.indexOf(active as HTMLAnchorElement) : -1;
    if (idx < 0 && active?.closest('.app-mega-menu-shell') === shell) {
      idx = 0;
    }

    if (key === 'ArrowDown') {
      event.preventDefault();
      const next = idx < 0 ? 0 : Math.min(idx + 1, links.length - 1);
      links[next]?.focus();
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      const prev = idx <= 0 ? 0 : idx - 1;
      links[prev]?.focus();
      return;
    }

    if (key === 'ArrowRight') {
      event.preventDefault();
      const from = idx >= 0 ? links[idx] : links[0];
      const lane = from?.closest('.app-mega-menu-lane');
      const nextLane = lane?.nextElementSibling as HTMLElement | null;
      if (nextLane) {
        const firstInNext = nextLane.querySelector<HTMLAnchorElement>(
          'a.app-mega-menu-link',
        );
        firstInNext?.focus();
        return;
      }
      const peer = this._peerRegistry.getAdjacent(
        this._elementRef.nativeElement,
        1,
      );
      peer?.openFromPeerNav('first');
      return;
    }

    if (key === 'ArrowLeft') {
      event.preventDefault();
      const from = idx >= 0 ? links[idx] : links[0];
      const lane = from?.closest('.app-mega-menu-lane');
      const prevLane = lane?.previousElementSibling as HTMLElement | null;
      if (prevLane) {
        const firstInPrev = prevLane.querySelector<HTMLAnchorElement>(
          'a.app-mega-menu-link',
        );
        firstInPrev?.focus();
        return;
      }
      const peer = this._peerRegistry.getAdjacent(
        this._elementRef.nativeElement,
        -1,
      );
      peer?.openFromPeerNav('lastLaneFirst');
    }
  }

  onPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  onLinkClick(): void {
    this._disposeOverlay();
  }

  onInternalLinkClick(event: MouseEvent, link: FuseNavigationItem): void {
    event.preventDefault();
    event.stopPropagation();
    this._workspaceTabs.navigateFromNavItem(link);
    this.onLinkClick();
  }

  triggerChangeDetection(): void {
    this._cdr.markForCheck();
  }

  isItemVisible(navItem: FuseNavigationItem): boolean {
    if (!navItem) {
      return false;
    }
    if (!navItem.hidden) {
      return true;
    }
    return !navItem.hidden(navItem);
  }

  trackCol(_index: number, col: MegaMenuColumn): string {
    return col.id;
  }

  trackLane(_index: number, lane: MegaMenuColumn[]): string {
    return lane.map((c) => c.id).join('|') || String(_index);
  }

  trackLink(_index: number, link: FuseNavigationItem): string {
    return link.id || link.title || String(_index);
  }

  private _disposeOverlay(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
    const wasOpen = this.megaOpen;
    this.megaOpen = false;
    if (wasOpen) {
      this._megaCoordinator.closed(this._closeBound);
      queueMicrotask(() => this.triggerRef?.nativeElement?.focus());
    }
    this._cdr.markForCheck();
  }

  /**
   * Programmatic focus does not trigger :focus-visible in Chromium, so we add a class
   * that mirrors the focus ring until the link blurs (then :focus-visible takes over).
   */
  private _focusMegaMenuLinkWithVisibleRing(
    el: HTMLAnchorElement | null | undefined,
  ): void {
    if (!el) {
      return;
    }
    el.classList.add('app-mega-menu-link--initial-focus');
    el.focus({ preventScroll: true });
    el.addEventListener(
      'blur',
      () => el.classList.remove('app-mega-menu-link--initial-focus'),
      { once: true },
    );
  }

  private _focusFirstMegaMenuLink(): void {
    const pane = this._overlayRef?.overlayElement;
    const shell = pane?.querySelector('.app-mega-menu-shell');
    if (!shell) {
      return;
    }
    const first = shell.querySelector<HTMLAnchorElement>('a.app-mega-menu-link');
    if (first) {
      this._focusMegaMenuLinkWithVisibleRing(first);
    } else {
      (shell as HTMLElement).focus();
    }
  }

  private _focusFirstLinkInLastLane(): void {
    const pane = this._overlayRef?.overlayElement;
    const shell = pane?.querySelector('.app-mega-menu-shell');
    if (!shell) {
      return;
    }
    const lanes = shell.querySelectorAll('.app-mega-menu-lane');
    const lastLane = lanes[lanes.length - 1];
    const link =
      lastLane?.querySelector<HTMLAnchorElement>('a.app-mega-menu-link') ??
      shell.querySelector<HTMLAnchorElement>('a.app-mega-menu-link');
    if (link) {
      this._focusMegaMenuLinkWithVisibleRing(link);
    } else {
      (shell as HTMLElement).focus();
    }
  }

  private _collectMegaMenuLinks(shell: Element): HTMLAnchorElement[] {
    return Array.from(
      shell.querySelectorAll<HTMLAnchorElement>('a.app-mega-menu-link'),
    );
  }

  private _rebuildColumns(): void {
    if (!this.item?.children?.length) {
      this.columns = [];
      this.lanes = [];
      return;
    }
    this.columns = this._buildColumnsFromChildren(this.item.children);
    this.lanes = this._splitIntoLanes(this.columns);
  }

  /**
   * Split ordered sections into side-by-side lanes so each lane height stays under a
   * viewport-based budget — more sections stack per lane than CSS flex-wrap alone,
   * and each lane is only as tall as its content (no empty band inside a fixed 300px box).
   */
  private _splitIntoLanes(cols: MegaMenuColumn[]): MegaMenuColumn[][] {
    if (!cols.length) {
      return [];
    }
    const maxLanePx = Math.min(
      560,
      Math.max(320, Math.floor(window.innerHeight * 0.52)),
    );
    const gapPx = 11;
    const lanes: MegaMenuColumn[][] = [];
    let current: MegaMenuColumn[] = [];
    let currentH = 0;

    for (const col of cols) {
      const h = this._estimateColumnHeightPx(col);
      if (current.length === 0) {
        current.push(col);
        currentH = h;
        continue;
      }
      if (currentH + gapPx + h > maxLanePx) {
        lanes.push(current);
        current = [col];
        currentH = h;
      } else {
        current.push(col);
        currentH += gapPx + h;
      }
    }
    if (current.length) {
      lanes.push(current);
    }
    return lanes;
  }

  /** Rough px height for lane budgeting (matches ~13px links + heading band). */
  private _estimateColumnHeightPx(col: MegaMenuColumn): number {
    const headingBand = 46;
    let h = headingBand;
    for (const sec of col.sections) {
      if (sec.subtitle) {
        h += 26;
      }
      h += Math.max(1, sec.links.length) * 28;
    }
    return h;
  }

  private _buildColumnsFromChildren(
    children: FuseNavigationItem[],
  ): MegaMenuColumn[] {
    const out: MegaMenuColumn[] = [];
    for (const child of children) {
      if (!this.isItemVisible(child)) {
        continue;
      }
      if (child.type === 'divider' || child.type === 'spacer') {
        continue;
      }
      if (child.type === 'basic') {
        out.push({
          id: child.id || child.title || `col-${out.length}`,
          title: child.title || '',
          sections: [{ links: [child] }],
        });
        continue;
      }
      if (
        child.type === 'aside' ||
        child.type === 'collapsable' ||
        child.type === 'group'
      ) {
        const sections = this._sectionsFromCollapsable(child);
        if (sections.length) {
          out.push({
            id: child.id || child.title || `col-${out.length}`,
            title: child.title || '',
            sections,
          });
        }
      }
    }
    return out;
  }

  private _sectionsFromCollapsable(
    node: FuseNavigationItem,
  ): MegaMenuSection[] {
    const sections: MegaMenuSection[] = [];
    const kids = node.children || [];
    const directBasics: FuseNavigationItem[] = [];
    const nested: FuseNavigationItem[] = [];

    for (const k of kids) {
      if (!this.isItemVisible(k)) {
        continue;
      }
      if (k.type === 'divider' || k.type === 'spacer') {
        continue;
      }
      if (k.type === 'basic') {
        directBasics.push(k);
      } else if (
        k.type === 'aside' ||
        k.type === 'collapsable' ||
        k.type === 'group'
      ) {
        nested.push(k);
      }
    }

    if (directBasics.length) {
      sections.push({ links: directBasics });
    }
    for (const n of nested) {
      const subLinks = this._flattenBasicLinks(n);
      if (subLinks.length) {
        sections.push({
          subtitle: n.title || undefined,
          links: subLinks,
        });
      }
    }
    return sections;
  }

  private _flattenBasicLinks(node: FuseNavigationItem): FuseNavigationItem[] {
    const acc: FuseNavigationItem[] = [];
    const walk = (n: FuseNavigationItem): void => {
      if (!this.isItemVisible(n)) {
        return;
      }
      if (n.type === 'basic' && n.link) {
        acc.push(n);
        return;
      }
      for (const c of n.children || []) {
        walk(c);
      }
    };
    walk(node);
    return acc;
  }

  private _updateHasActiveChild(url: string): void {
    if (!this.item) {
      return;
    }
    const nav = this._fuseNavigationService.getComponent<{
      navigation?: FuseNavigationItem[];
    }>(this.name);
    this.hasActiveChild = this._itemHasActiveDescendant(this.item, url, nav?.navigation);
    this._cdr.markForCheck();
  }

  /**
   * True when this module owns the most specific link for the current URL.
   * A shorter prefix in another module (Utilities → `/reports`) does not stay active
   * once a longer link exists (Reports → `/reports/customer-ledger`).
   */
  private _itemHasActiveDescendant(
    item: FuseNavigationItem,
    currentUrl: string,
    navigation: FuseNavigationItem[] | undefined,
  ): boolean {
    const own = Math.max(
      this._bestMatchLength(item.children, currentUrl),
      typeof item.link === 'string'
        ? this._linkMatchLength(currentUrl, item.link, item.exactMatch === true)
        : 0,
    );
    if (own <= 0) {
      return false;
    }
    const tree = navigation?.length ? navigation : [item];
    return own === this._bestMatchLength(tree, currentUrl);
  }

  /** Longest menu-link match in this tree. 0 when nothing matches. */
  private _bestMatchLength(
    items: FuseNavigationItem[] | undefined,
    currentUrl: string,
  ): number {
    let best = 0;
    const walk = (nodes: FuseNavigationItem[] | undefined): void => {
      for (const node of nodes || []) {
        if (!this.isItemVisible(node)) {
          continue;
        }
        if (typeof node.link === 'string') {
          best = Math.max(
            best,
            this._linkMatchLength(currentUrl, node.link, node.exactMatch === true),
          );
        }
        walk(node.children);
      }
    };
    walk(items);
    return best;
  }

  /**
   * How specifically `link` matches the current URL. Longer paths win.
   * `/` matches only the home URL, so a root link cannot light up every module.
   */
  private _linkMatchLength(currentUrl: string, link: string, exact = false): number {
    if (!link) {
      return 0;
    }
    const path = this._navigationPathname(currentUrl);
    const linkPath = this._navigationPathname(link);
    if (linkPath === '/') {
      return path === '/' ? 1 : 0;
    }
    if (path === linkPath) {
      return linkPath.length;
    }
    if (!exact && path.startsWith(`${linkPath}/`)) {
      return linkPath.length;
    }
    return 0;
  }

  /** Path only, so `?__tab=` and hashes do not affect which module is active. */
  private _navigationPathname(url: string): string {
    const raw = (url || '').split(/[?#]/)[0] || '/';
    const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
    if (withSlash.length > 1 && withSlash.endsWith('/')) {
      return withSlash.slice(0, -1);
    }
    return withSlash;
  }
}
